import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DiContainer } from '@/di/di-container';
import { ClientStorageService } from '@/common/services/client-storage.service';
import { useGetPickerDetailsQuery } from '@/services/picker.service';
import { useGetAvailableBunkersQuery } from '@/services/configurator.service';
import { useGetSettingsQuery } from '@/services/settings.service';
import { Button, Spin } from 'antd';
import { BunkerCard } from '@/configurator/components/Catalog/components/BunkerCard';
import { flushQuantities } from '@/configurator/helpers/quantity-flush';

const clientStorageService = DiContainer.get(ClientStorageService);

export function ConfiguratorView() {
  const uuid = clientStorageService.getUUID();
  const navigate = useNavigate();

  const { data: bunkers, isLoading } = useGetAvailableBunkersQuery({});
  const { data: settings } = useGetSettingsQuery();
  const showPrices = !!settings?.show_prices;
  const { data: picker, refetch } = useGetPickerDetailsQuery(uuid);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    refetch();
  }, []);

  if (isLoading) {
    return (
      <Spin
        className="flex h-full justify-center items-center"
        spinning={true}
        size="large"></Spin>
    );
  }

  // Выбранные позиции (по одной на модель). first?.id — picker.id для перехода в Эксперт.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selected: any[] = picker?.data || [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const positionFor = (bunkerId: number) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selected.find(
      (item: any) =>
        item.bunker.id === bunkerId && (!item.configured || !item.can_configured)
    );

  const goToConfigurator = async () => {
    // Дождаться, пока отложенные (debounce) изменения количества доедут до бэка,
    // иначе групповой Эксперт подтянет устаревший набор.
    setNavigating(true);
    await flushQuantities();
    navigate('/options');
  };

  return (
    <div className="container pb-[120px]">
      <h2 className="font-semibold text-[20px] mb-[24px]">
        Выберите модели бункеров-перегрузчиков и их количество
      </h2>
      {/* 2 карточки в ряд на десктопе, 1 колонка на мобиле */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {bunkers?.data?.map((b: any, i: number) => {
          const position = positionFor(+b.id);
          return (
            <div key={b.id}>
              <BunkerCard
                idx={i}
                id={+b.id}
                canConfigured={b?.attributes?.can_configured}
                pickerId={position?.id || 0}
                model={b?.attributes?.bunker_model}
                description={b?.attributes?.description}
                technicalParams={b?.attributes?.technical_params}
                count={position?.quantity || 0}
                image={b?.attributes?.bunker_image_url || ''}
                price={showPrices ? b?.attributes?.current_price : undefined}
              />
            </div>
          );
        })}
      </div>

      {selected.length > 0 && (
        // sticky: держится у низа экрана и паркуется над футером (не лезет в него).
        // Кнопку сдвигаем к правому краю окна отрицательным margin (без растяжения обёртки —
        // иначе появлялась горизонтальная прокрутка). 100% здесь = ширина .container.
        <div className="sticky bottom-6 z-40 flex justify-end pointer-events-none">
          <Button
            type="primary"
            size="large"
            loading={navigating}
            className="pointer-events-auto shadow-lg"
            style={{
              background: '#66B28A',
              height: 56,
              paddingInline: 32,
              marginRight: 'calc(2.5rem - (100vw - 100%) / 2)',
            }}
            onClick={goToConfigurator}>
            Конфигурировать опции
          </Button>
        </div>
      )}
    </div>
  );
}
